/**
 * Builds the photo half of an `updateWaypoint` payload.
 *
 * The dialog used to re-send `file.url` for every photo already on the waypoint
 * alongside the new ones. The server treats each URL as a photo to fetch and
 * attach, so every save duplicated the existing photos — and a photo whose file
 * is missing reports the server's placeholder URL, which 404s and failed the
 * whole mutation. New photos now travel as Active Storage signed IDs and photos
 * already on the waypoint are simply left alone.
 *
 * Pure and deterministic so the wire format can be unit-tested without the DOM.
 */

export interface WaypointPhotoLike {
  id?: string | number | null;
  signedId?: string | null;
}

export interface WaypointPhotoPayload {
  imageSignedIds: string[];
}

export function isPendingPhotoId(id: unknown): boolean {
  return String(id ?? '').startsWith('temp_');
}

export function buildWaypointPhotoPayload(
  pendingUploads: WaypointPhotoLike[] = [],
  photosToRemove: WaypointPhotoLike[] = []
): WaypointPhotoPayload {
  const removedSignedIds = new Set(
    photosToRemove.map((photo) => photo?.signedId).filter(Boolean) as string[]
  );

  const imageSignedIds: string[] = [];
  for (const upload of pendingUploads) {
    const signedId = upload?.signedId;
    if (!signedId) continue;
    if (removedSignedIds.has(signedId)) continue;
    if (imageSignedIds.includes(signedId)) continue;
    imageSignedIds.push(signedId);
  }

  return { imageSignedIds };
}

/**
 * Photos the user staged for deletion that actually exist server-side. Pending
 * uploads (`temp_` ids) were never persisted, so they need no destroy call.
 */
export function persistedPhotosToDestroy<T extends WaypointPhotoLike>(photosToRemove: T[] = []): T[] {
  return photosToRemove.filter((photo) => photo?.id && !isPendingPhotoId(photo.id));
}
